import type { PDFPageTextContent } from '@affine/core/modules/pdf';
import type { PdfPageSearchQuery } from '@affine/graphql';

import type { PDFPageHighlightRect } from '../../../modules/pdf/views/pdf-highlight-overlay';
import type { PDFPageSearchHit } from './pdf-find-controller';
import {
  createGlyphRangeRects,
  createSearchableGlyphText,
  normalizePdfSearchText,
} from './pdf-local-find-provider';

export function mapPdfSearchHits(
  result: PdfPageSearchQuery,
  attachmentBlockId: string
): PDFPageSearchHit[] {
  return result.workspace.pdfSearch
    .filter(hit => hit.attachmentBlockId === attachmentBlockId)
    .map(hit => ({
      docId: hit.docId,
      attachmentBlockId: hit.attachmentBlockId,
      pageIndex: hit.pageIndex,
      source: hit.source,
      coverageStatus: hit.coverageStatus,
      snippet: hit.snippet,
      score: hit.score,
      rects: hit.rects.map(rect => ({
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
      })),
    }));
}

export function getPageHighlightRects({
  hits,
  activeHit,
  pageIndex,
  isThumbnail,
  query,
  text,
}: {
  hits: PDFPageSearchHit[];
  activeHit: PDFPageSearchHit | null;
  pageIndex: number;
  isThumbnail?: boolean;
  query?: string;
  text?: PDFPageTextContent | null;
}): PDFPageHighlightRect[] {
  if (isThumbnail) return [];

  const pageHits = hits.filter(hit => hit.pageIndex === pageIndex);
  const backendRects = pageHits.flatMap(hit =>
    hit.rects.map(rect => ({
      ...rect,
      active: hit === activeHit,
      kind: 'search' as const,
    }))
  );
  const exactTextRects = getTextLayerSearchRects({
    hits: pageHits,
    activeHit,
    query,
    text,
  });

  if (exactTextRects.length === 0) return backendRects;

  const ocrRects = pageHits
    .filter(hit => hit.source === 'ocr')
    .flatMap(hit =>
      hit.rects.map(rect => ({
        ...rect,
        active: hit === activeHit,
        kind: 'search' as const,
      }))
    );

  return [...exactTextRects, ...ocrRects];
}

function getTextLayerSearchRects({
  activeHit,
  hits,
  query,
  text,
}: {
  activeHit: PDFPageSearchHit | null;
  hits: PDFPageSearchHit[];
  query?: string;
  text?: PDFPageTextContent | null;
}): PDFPageHighlightRect[] {
  const normalizedQuery = normalizePdfSearchText(query ?? '');
  if (!text || normalizedQuery.length === 0) return [];

  const searchCandidateHits = hits.filter(hit => hit.source !== 'ocr');
  if (searchCandidateHits.length === 0) return [];
  const textLayerHits = searchCandidateHits.filter(
    hit => hit.source !== 'navigation'
  );

  const { glyphIndexBySearchChar, searchableText } = createSearchableGlyphText(
    text.glyphs
  );

  const matches: Array<{
    rects: Array<Pick<PDFPageHighlightRect, 'x' | 'y' | 'width' | 'height'>>;
  }> = [];
  let offset = 0;

  while (offset <= searchableText.length - normalizedQuery.length) {
    const matchStart = searchableText.indexOf(normalizedQuery, offset);
    if (matchStart === -1) break;
    const matchEnd = matchStart + normalizedQuery.length;
    const glyphIndexes = Array.from(
      new Set(glyphIndexBySearchChar.slice(matchStart, matchEnd))
    );
    matches.push({
      rects: createGlyphRangeRects(
        glyphIndexes.map(index => text.glyphs[index]).filter(Boolean)
      ),
    });
    offset = matchStart + Math.max(1, normalizedQuery.length);
  }

  const activeMatchIndex = getActiveTextLayerMatchIndex({
    activeHit,
    matches,
    textLayerHits,
  });

  return matches.flatMap((match, matchIndex) =>
    match.rects.map(rect => ({
      ...rect,
      active: matchIndex === activeMatchIndex,
      kind: 'search' as const,
    }))
  );
}

function getActiveTextLayerMatchIndex({
  activeHit,
  matches,
  textLayerHits,
}: {
  activeHit: PDFPageSearchHit | null;
  matches: Array<{
    rects: Array<Pick<PDFPageHighlightRect, 'x' | 'y' | 'width' | 'height'>>;
  }>;
  textLayerHits: PDFPageSearchHit[];
}) {
  if (!activeHit || matches.length === 0) return -1;

  const textLayerHitIndex =
    activeHit.source !== 'navigation' ? textLayerHits.indexOf(activeHit) : -1;
  if (textLayerHitIndex >= 0) return textLayerHitIndex;

  if (activeHit.source !== 'navigation') return -1;

  const overlappingMatchIndex = findBestOverlappingMatchIndex(
    matches,
    activeHit
  );
  return overlappingMatchIndex >= 0 ? overlappingMatchIndex : 0;
}

function findBestOverlappingMatchIndex(
  matches: Array<{
    rects: Array<Pick<PDFPageHighlightRect, 'x' | 'y' | 'width' | 'height'>>;
  }>,
  hit: PDFPageSearchHit
) {
  let bestIndex = -1;
  let bestScore = 0;

  matches.forEach((match, index) => {
    const score = Math.max(
      0,
      ...match.rects.flatMap(matchRect =>
        hit.rects.map(hitRect => getRectOverlapScore(matchRect, hitRect))
      )
    );
    if (score > bestScore) {
      bestIndex = index;
      bestScore = score;
    }
  });

  return bestIndex;
}

function getRectOverlapScore(
  a: Pick<PDFPageHighlightRect, 'x' | 'y' | 'width' | 'height'>,
  b: Pick<PDFPageHighlightRect, 'x' | 'y' | 'width' | 'height'>
) {
  const left = Math.max(a.x, b.x);
  const top = Math.max(a.y, b.y);
  const right = Math.min(a.x + a.width, b.x + b.width);
  const bottom = Math.min(a.y + a.height, b.y + b.height);
  const intersection = Math.max(0, right - left) * Math.max(0, bottom - top);
  if (intersection <= 0) return 0;

  const aArea = a.width * a.height;
  return intersection / Math.max(0.000001, aArea);
}

export function createNavigationPdfSearchHit({
  docId,
  attachmentBlockId,
  pageIndex,
  query,
  rects,
}: {
  docId: string;
  attachmentBlockId: string;
  pageIndex: number;
  query?: string;
  rects: PDFPageHighlightRect[];
}): PDFPageSearchHit {
  return {
    docId,
    attachmentBlockId,
    pageIndex,
    source: 'navigation',
    coverageStatus: null,
    snippet: query ?? '',
    score: 1,
    rects: rects.map(({ x, y, width, height }) => ({ x, y, width, height })),
  };
}
